import { computed, unref } from "vue";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { API } from "@/services/apiInstance";
import { store } from "@/store";
import { chatAppActive } from "@/services/chatActivity";
import { operationalFreshness } from "./queryFreshness";
import { mergeEntityIntoPagedListCaches } from "@/services/queryCacheMerge";

import { Flags, hasFeatureFlag, reconcileFeatureDenied } from "@/classes/FeatureFlags";

export function chatReadOnly() {
    return store.getters.tokenUserID() === "ADMIN OVERRIDE";
}

function assertChatWritable() {
    if (chatReadOnly()) {
        throw Object.assign(new Error("Support chat access is read only"), { status: 403 });
    }
}

export function chatAccessEnabled() {
    return (
        Boolean(store.state.token) &&
        hasFeatureFlag(Flags.TeamChatModule) &&
        ["admin", "user"].includes(store.getters.realUserRole())
    );
}

export const chatKeys = {
    all: ["chat"],
    groups: (actor, status) => ["chat", "groups", actor, status],
    summary: (tenant, actor) => ["chat", "summary", tenant, actor],
    messages: (actor, id, around = "") =>
        around ? ["chat", "messages", actor, id, around] : ["chat", "messages", actor, id],
    members: (tenant, actor, id, search) => ["chat", "members", tenant, actor, id, search]
};

export function useChatGroups(status = "active") {
    return useQuery({
        queryKey: computed(() => chatKeys.groups(store.getters.tokenUserID(), unref(status))),
        queryFn: ({ signal }) => API.Chat.List({ status: unref(status) }, { signal }),
        enabled: computed(chatAccessEnabled),
        ...operationalFreshness,
        refetchInterval: computed(() => (chatAppActive.value ? 10_000 : false))
    });
}

export function useChatMessages(id, around = "") {
    return useInfiniteQuery({
        queryKey: computed(() =>
            chatKeys.messages(store.getters.tokenUserID(), unref(id), unref(around))
        ),
        queryFn: ({ pageParam, signal }) =>
            API.Chat.MessagesPage(
                unref(id),
                { page_token: pageParam, ...(unref(around) ? { around: unref(around) } : {}) },
                { signal }
            ),
        initialPageParam: "",
        getNextPageParam: (page) => page.next_page_token || undefined,
        enabled: computed(() => chatAccessEnabled() && Boolean(unref(id))),
        ...operationalFreshness,
        // A fallback for lost events, disconnected sockets and native resume.
        refetchInterval: computed(() => (chatAppActive.value ? 10_000 : false)),
        retry: (count, error) => ![403, 404].includes(error?.status) && count < 2
    });
}

export function useChatMembers(id, search, enabled) {
    return useInfiniteQuery({
        queryKey: computed(() =>
            chatKeys.members(
                store.state.customerID,
                store.getters.tokenUserID(),
                unref(id),
                unref(search)
            )
        ),
        queryFn: ({ pageParam, signal }) =>
            API.Chat.MembersPage(
                unref(id),
                { search: unref(search), page_token: pageParam },
                { signal }
            ),
        initialPageParam: "",
        getNextPageParam: (page) => page.next_page_token || undefined,
        enabled: computed(() => chatAccessEnabled() && Boolean(unref(id)) && unref(enabled)),
        ...operationalFreshness,
        refetchInterval: computed(() => (chatAppActive.value ? 10_000 : false)),
        retry: (count, error) => ![403, 404].includes(error?.status) && count < 2
    });
}

export function useChatMutations() {
    const client = useQueryClient();
    const invalidate = () =>
        client.invalidateQueries({ queryKey: chatKeys.all }, { cancelRefetch: false });
    const create = useMutation({
        mutationFn: ({ payload, key }) => {
            assertChatWritable();
            return API.Chat.Add(payload, key);
        },
        onSuccess: invalidate
    });
    const update = useMutation({
        mutationFn: ({ id, payload }) => {
            assertChatWritable();
            return API.Chat.Update(id, payload);
        },
        onSuccess: invalidate
    });
    const send = useMutation({
        mutationFn: ({ id, payload, key }) => {
            assertChatWritable();
            return API.Chat.SendMessage(id, payload, key);
        },
        onSuccess: (message, { id }) => {
            mergeEntityIntoPagedListCaches(client, {
                queryKey: chatKeys.messages(store.getters.tokenUserID(), id),
                entity: message,
                matches: (key, row) => key.length === 4 && row.group_id === id,
                sort: (a, b) => b.sequence - a.sequence
            });
            void invalidate();
        }
    });
    return { create, update, send };
}

export function useChatSummary() {
    const enabled = computed(
        () =>
            chatAccessEnabled() &&
            /^[a-f0-9]{24}$/i.test(store.getters.tokenUserID() || "") &&
            ["admin", "user"].includes(store.getters.realUserRole())
    );
    const query = useQuery({
        queryKey: computed(() =>
            chatKeys.summary(store.state.customerID, store.getters.tokenUserID())
        ),
        queryFn: async ({ signal }) => {
            const tenant = store.state.customerID,
                actor = store.getters.tokenUserID();
            try {
                return await API.Chat.Summary({ signal });
            } catch (error) {
                if (tenant === store.state.customerID && actor === store.getters.tokenUserID())
                    reconcileFeatureDenied(Flags.TeamChatModule, error);
                throw error;
            }
        },
        enabled,
        ...operationalFreshness,
        refetchInterval: computed(() => (chatAppActive.value ? 10_000 : false)),
        retry: (count, error) => ![403, 404].includes(error?.status) && count < 2
    });
    const rows = computed(() =>
        enabled.value && ![403, 404].includes(query.error.value?.status)
            ? query.data.value || []
            : []
    );
    const unread = computed(() =>
        rows.value
            .filter((row) => row.unread && row.member)
            .sort((a, b) => b.latest.created_at - a.latest.created_at)
    );
    const count = computed(() => unread.value.length);
    const byGroup = computed(() =>
        Object.fromEntries(rows.value.map((row) => [row.group_id, row]))
    );
    return { query, enabled, rows, unread, count, byGroup };
}

export function useChatStateMutation() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }) => {
            assertChatWritable();
            return API.Chat.UpdateState(id, payload);
        },
        onMutate: () => ({
            key: chatKeys.summary(store.state.customerID, store.getters.tokenUserID())
        }),
        onSuccess: (state, { id, payload }, scope) => {
            const key = scope.key;
            if (key[2] !== store.state.customerID || key[3] !== store.getters.tokenUserID()) return;
            client.setQueryData(key, (rows) =>
                rows?.map((row) =>
                    row.group_id === id
                        ? {
                              ...row,
                              state: {
                                  ...row.state,
                                  read_sequence: Math.max(
                                      row.state.read_sequence || 0,
                                      state.read_sequence
                                  ),
                                  ...("mention_read_sequence" in payload
                                      ? {
                                            mention_read_sequence: Math.max(
                                                row.state.mention_read_sequence || 0,
                                                state.mention_read_sequence || 0
                                            )
                                        }
                                      : {}),
                                  ...("muted" in payload ? { muted: state.muted } : {})
                              },
                              mention:
                                  row.mention &&
                                  row.mention.sequence >
                                      Math.max(
                                          row.state.read_sequence || 0,
                                          state.read_sequence || 0,
                                          row.state.mention_read_sequence || 0,
                                          state.mention_read_sequence || 0
                                      )
                                      ? row.mention
                                      : null,
                              unread: row.latest
                                  ? row.latest.sequence >
                                    Math.max(row.state.read_sequence || 0, state.read_sequence)
                                  : false
                          }
                        : row
                )
            );
            void client.invalidateQueries({ queryKey: key });
        }
    });
}
